/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Button, Dialog, Typography2 } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import type { AIMessageDraft } from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/types';
import EmailPreviewBody from 'bundles/enterprise-admin-messaging-v2/components/common/EmailPreviewBody';
import { useMessagingTrackingData } from 'bundles/enterprise-admin-messaging-v2/hooks/useMessagingTrackingData';
import { NO_REPLY_EMAIL } from 'bundles/enterprise-admin-messaging-v2/hooks/useSendMessage';
import { MESSAGING_MODAL } from 'bundles/enterprise-admin-messaging-v2/utils/eventing';
import { useThirdPartyOrganization } from 'bundles/enterprise-admin/ThirdPartyOrganizationProvider';

import _t from 'i18n!nls/enterprise-admin-messaging-v2';

type Props = {
  draft: AIMessageDraft;
  /** Send action. Omit for a read-only preview (no actions bar). */
  onSend?: () => void;
  /** Back action. Omit for a read-only preview (no actions bar). */
  onBack?: () => void;
  /**
   * Fetches the server-rendered email HTML — the same render the backend sends.
   * When provided, the screen creates a draft and renders the API's HTML (with a
   * loading/error state). When omitted, the screen shows a loading state until
   * a fetcher is wired (e.g. while the audience is still resolving).
   */
  fetchPreviewHtml?: () => Promise<string>;
  /** True while the message is being sent — drives the Send button spinner. */
  sending?: boolean;
  /** Error surfaced when the send fails, so the admin gets feedback and can retry. */
  sendError?: string | null;
};

const styles = {
  actions: css`
    display: flex;
    align-items: center;
    gap: var(--cds-spacing-150);
    width: 100%;
  `,
  sendError: css`
    margin-bottom: var(--cds-spacing-100);
    color: var(--cds-color-feedback-error);

    /* Keep the alert region in the DOM for screen readers; collapse it when empty. */
    :empty {
      display: none;
    }
  `,
};

/**
 * Preview-and-send sub-screen. Renders the compiled email inside a sandboxed
 * iframe via {@link EmailPreviewBody}. Send transitions to the Sent
 * confirmation; Back returns to Review with the draft preserved. With neither
 * handler the preview is read-only (no actions bar) — e.g. the "See preview"
 * link on the resend success toast.
 */
const PreviewEmailScreen = ({ draft, onSend, onBack, fetchPreviewHtml, sending = false, sendError = null }: Props) => {
  const [serverHtml, setServerHtml] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(Boolean(fetchPreviewHtml));
  const [hasError, setHasError] = React.useState(false);

  React.useEffect(() => {
    if (!fetchPreviewHtml) return undefined;
    let cancelled = false;
    setIsLoading(true);
    setHasError(false);

    const fetchHtml = async () => {
      try {
        const result = await fetchPreviewHtml();
        if (!cancelled) {
          setServerHtml(result);
          setIsLoading(false);
        }
      } catch {
        if (!cancelled) {
          setHasError(true);
          setIsLoading(false);
        }
      }
    };

    fetchHtml();
    return () => {
      cancelled = true;
    };
  }, [fetchPreviewHtml]);

  const track = useTracker();
  const trackingData = useMessagingTrackingData();
  // Falls back to the placeholder when the org context hasn't loaded (e.g. in
  // isolation in tests) — matches the real senderName sent by useSendMessage.
  const orgName = useThirdPartyOrganization()?.name || _t('Your Organization');

  // Modal impression. Fires for both the in-flow preview and the read-only
  // "See preview" dialog — both render the same `messaging_preview_email` modal.
  // Send / Back are intentionally not tracked here: the AIMessageModal
  // orchestrator owns the send marker, and Back is a dismiss it handles.
  React.useEffect(() => {
    track('view_modal', { ...trackingData, modal: { name: MESSAGING_MODAL.PREVIEW_EMAIL } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <React.Fragment>
      <Dialog.Content>
        <EmailPreviewBody
          subject={draft.subject}
          html={serverHtml}
          fromName={orgName}
          fromEmail={NO_REPLY_EMAIL}
          toAddress={_t('Learner email address')}
          loading={isLoading}
          error={hasError}
          dataTestIdPrefix="ai-message-modal-preview"
        />
      </Dialog.Content>
      {onSend || onBack ? (
        <Dialog.Actions>
          {/* Persistent live region so a send failure is announced + visible. */}
          <Typography2 component="p" variant="bodySecondary" role="alert" css={styles.sendError}>
            {sendError}
          </Typography2>
          <div css={styles.actions}>
            {onSend ? (
              <Button variant="primary" onClick={onSend} loading={sending} data-testid="ai-message-modal-preview-send">
                {_t('Send')}
              </Button>
            ) : null}
            {onBack ? (
              <Button variant="ghost" onClick={onBack} data-testid="ai-message-modal-preview-back">
                {_t('Back')}
              </Button>
            ) : null}
          </div>
        </Dialog.Actions>
      ) : null}
    </React.Fragment>
  );
};

export default PreviewEmailScreen;
